import React, { useState, useRef } from 'react';
import './Onboarding.css';
import { FiArrowRight, FiCheckCircle, FiAlertCircle, FiLoader } from 'react-icons/fi';
import AddressAutocomplete from '../components/AddressAutocomplete';
import emailjs from '@emailjs/browser';
import { APP_CONFIG } from '../config.js';

const INITIAL_FORM_STATE = {
    name: '',
    business_number: '',
    contact_person: '',
    contact_email: '',
    email: '',
    address: '',
    city: '',
    province: 'ON',
    postal_code: '',
    phone: '',
};

const Onboarding = () => {
    const formRef = useRef();
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSameEmail, setIsSameEmail] = useState(true);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handlePlaceSelected = (place) => {
        setFormData(prevState => ({
            ...prevState,
            address: place.address,
            city: place.city,
            province: place.province,
            postal_code: place.postal_code,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        setError('');

        // The 'role' is not part of the form, so we can't use emailjs.sendForm.
        // Instead, we'll use emailjs.send and pass the form data along with the role.
        const templateParams = {
            ...formData, // Spread all form fields
            email: isSameEmail ? formData.contact_email : formData.email, // Set correct login email
            role: 'l1', // Add the default role
        };

        try {
            const { service, onboarding_template, publicKey } = APP_CONFIG.emailJs;
            await emailjs.send(service, onboarding_template, templateParams, publicKey);

            setMessage('Your onboarding request has been sent successfully!');
            setFormData(INITIAL_FORM_STATE); // Reset form on success
            // Note: formRef.current.reset() is not needed as we control state with React.
        } catch (err) {
            console.error('EmailJS Failed:', err);
            setError(err.text || 'Failed to send onboarding request. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center justify-center text-center overflow-hidden">
                <img
                    src="/PayrollHero.png"
                    alt="Onboarding with Oriental Business Solutions"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1e45] via-[#0d1e45]/80 to-transparent" />
                <div className="relative z-10 max-w-4xl mx-auto px-6">
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-pto-yellow/40 bg-pto-yellow/10 text-pto-yellow text-xs font-bold uppercase tracking-widest mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-pto-yellow animate-pulse" />
                        Get Started
                    </span>
                    <h1 className="text-5xl md:text-6xl font-black text-white leading-tight mb-4">
                        Welcome to OrientalPay
                    </h1>
                    <p className="text-blue-100 text-base md:text-lg font-medium leading-relaxed max-w-2xl mx-auto">
                        Let's get your business set up for simple, reliable payroll. Just fill out the form below and we'll take care of the rest.
                    </p>
                </div>
            </section>

            {/* Form Section */}
            <section className="section-pastel-blue py-16 px-4 sm:px-6 lg:px-8 -mt-20">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                        <div className="px-6 py-8 sm:p-10">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl md:text-3xl font-black text-gray-900">Create Your Account</h2>
                                <p className="text-gray-500 mt-2 text-sm font-medium">Join our platform by providing your company details below.</p>
                            </div>

                            {message && (
                                <div className="flex items-center gap-3 bg-green-50 text-green-700 border border-green-200 rounded-xl p-4 mb-6 text-sm font-semibold">
                                    <FiCheckCircle className="text-lg" />
                                    <span>{message}</span>
                                </div>
                            )}
                            {error && (
                                <div className="flex items-center gap-3 bg-red-50 text-red-700 border border-red-200 rounded-xl p-4 mb-6 text-sm font-semibold">
                                    <FiAlertCircle className="text-lg" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <form ref={formRef} onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                <div className="form-group"><label htmlFor="name">Company Name</label><input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required /></div>
                                <div className="form-group"><label htmlFor="business_number">Business Number (BN)</label><input type="text" id="business_number" name="business_number" value={formData.business_number} onChange={handleChange} required /></div>
                                <div className="form-group"><label htmlFor="contact_person">Primary Contact Name</label><input type="text" id="contact_person" name="contact_person" value={formData.contact_person} onChange={handleChange} required /></div>
                                <div className="form-group"><label htmlFor="phone">Contact Phone</label><input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required /></div>

                                <div className="md:col-span-2 border-t border-gray-100 pt-6">
                                    <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Mailing Address</p>
                                </div>

                                <div className="form-group md:col-span-2">
                                    <label htmlFor="address">Street Address</label>
                                    <AddressAutocomplete id="address" className="form-control" value={formData.address} onChange={handleChange} onPlaceSelected={handlePlaceSelected} placeholder="Start typing your address..." />
                                </div>

                                <div className="form-group"><label htmlFor="city">City</label><input type="text" id="city" name="city" value={formData.city} onChange={handleChange} required /></div>
                                <div className="form-group"><label htmlFor="postal_code">Postal Code</label><input type="text" id="postal_code" name="postal_code" value={formData.postal_code} onChange={handleChange} required /></div>

                                <div className="form-group md:col-span-2">
                                    <label htmlFor="province">Province</label>
                                    <select id="province" name="province" value={formData.province} onChange={handleChange} required>
                                        <option value="ON">Ontario</option>
                                        <option value="BC">British Columbia</option>
                                        <option value="AB">Alberta</option>
                                        <option value="QC">Quebec</option>
                                        <option value="MB">Manitoba</option>
                                        <option value="SK">Saskatchewan</option>
                                        <option value="NS">Nova Scotia</option>
                                        <option value="NB">New Brunswick</option>
                                        <option value="NL">Newfoundland and Labrador</option>
                                        <option value="PE">Prince Edward Island</option>
                                        <option value="NT">Northwest Territories</option>
                                        <option value="NU">Nunavut</option>
                                        <option value="YT">Yukon</option>
                                    </select>
                                </div>

                                <div className="md:col-span-2 border-t border-gray-100 pt-6">
                                    <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Login Details</p>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="contact_email">Contact Email</label>
                                    <input type="email" id="contact_email" name="contact_email" value={formData.contact_email} onChange={handleChange} required />
                                </div>

                                {!isSameEmail && (
                                    <div className="form-group">
                                        <label htmlFor="email">Login Email Address</label>
                                        <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required={!isSameEmail} />
                                    </div>
                                )}

                                <div className="form-check md:col-span-2">
                                    <input className="form-check-input" type="checkbox" id="sameAsLogin" checked={isSameEmail} onChange={(e) => setIsSameEmail(e.target.checked)} />
                                    <label className="form-check-label" htmlFor="sameAsLogin">Use contact email as login email</label>
                                </div>

                                <div className="md:col-span-2 mt-4">
                                    <button type="submit" className="w-full btn-payday-blue text-base py-3.5 flex items-center justify-center gap-2" disabled={isLoading}>
                                        {isLoading ? (
                                            <>
                                                <FiLoader className="animate-spin" />
                                                <span>Creating Account...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>Create Account</span>
                                                <FiArrowRight />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Onboarding;
